import { useToast } from "@/hooks/use-toast";
import { useDeletePriority } from "@/hooks/useDeletePriority";
import { Priority } from "@/types";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";

interface PriorityDeleteDialogProps {
    priority: Priority | null;
    onOpenChange: (open: boolean) => void;
}

export function PriorityDeleteDialog({ priority, onOpenChange }: PriorityDeleteDialogProps) {
    const { toast } = useToast();
    const del = useDeletePriority();
    const confirm = () => priority && del.mutate(priority.pid, {
        onSuccess: () => {
            toast({ title: "Deliverable deleted", description: "The deliverable has been successfully deleted." });
            onOpenChange(false);
        },
        onError: () => toast({ title: "Error", description: "Failed to delete. Please try again.", variant: "destructive" }),
    });

    return (
        <AlertDialog open={!!priority} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete
                        <span className="font-semibold text-foreground"> {priority?.name} </span>
                        and remove it from our servers.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={confirm}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={del.isPending}
                    >
                        {del.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : "Delete"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
